import { useI18n } from '../i18n/useI18n'
import { localizedText, priceTag, toggleOption, type VariationGroup, type VariationSelection } from '../lib/menuVariation'

interface VariationPickerProps {
  groups: VariationGroup[]
  selection: VariationSelection
  onChange: (next: VariationSelection) => void
}

/**
 * #2031 商品を開いたときに出す「塩／たれ」「トッピング」の選択。
 *
 * - **必須かどうかをグループの見出しに出す。** 押せないボタンだけ見せると、客は
 *   なぜカートに入らないのか分からない
 * - 売り切れの選択肢は**消さずに押せない形で残す**。消すと「味噌があったはず」と
 *   客が探し続ける
 * - 追加料金のある選択肢には `+¥100` を出す。無料の選択 (塩／たれ) には何も付けない
 * - #2156 **名前は客の言語で出す。** 商品名だけ訳されて「味: 塩」が日本語のままだと、
 *   外国のお客様はそもそも選べない。訳が無ければ日本語に落ちる
 */
export default function VariationPicker({ groups, selection, onChange }: VariationPickerProps) {
  const { t, locale } = useI18n()
  if (groups.length === 0) return null
  return (
    <div className="mt-4 space-y-3" data-testid="variation-picker">
      {groups.map((group) => {
        const picked = selection[group.id] ?? []
        return (
          <fieldset key={group.id}>
            <legend className="flex items-center gap-1.5 font-body text-xs font-semibold text-text-primary">
              {localizedText(group, 'name', group.name, locale)}
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                  group.required ? 'bg-yokoso-red/10 text-yokoso-red' : 'bg-gray-100 text-text-tertiary'
                }`}
              >
                {t(group.required ? 'variation.required' : 'variation.optional')}
              </span>
              {group.select === 'multi' && (
                <span className="font-normal text-[10px] text-text-tertiary">{t('variation.multi')}</span>
              )}
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {group.options.map((option) => {
                const active = picked.includes(option.id)
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={option.soldOut}
                    aria-pressed={active}
                    onClick={() => onChange(toggleOption(group, selection, option.id))}
                    className={`min-h-9 rounded-full border px-3 font-jp text-sm tap-effect transition-colors ${
                      option.soldOut
                        ? 'border-gray-100 bg-gray-50 text-text-tertiary'
                        : active
                          ? 'border-yokoso-red bg-yokoso-red text-white'
                          : 'border-gray-200 bg-white text-text-primary'
                    }`}
                  >
                    {/* 取り消し線は名前だけ。ボタンごと引くと「売り切れ」の文字まで消し線になる */}
                    <span className={option.soldOut ? 'line-through' : undefined}>
                      {localizedText(option, 'label', option.label, locale)}
                    </span>
                    {!option.soldOut && priceTag(option.priceDelta) && (
                      <span className={`ml-1 text-xs ${active ? 'text-white/85' : 'text-text-tertiary'}`}>
                        {priceTag(option.priceDelta)}
                      </span>
                    )}
                    {option.soldOut && (
                      <span className="ml-1 text-[10px]">{t('storeDetail.soldOut')}</span>
                    )}
                  </button>
                )
              })}
            </div>
          </fieldset>
        )
      })}
    </div>
  )
}
